import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth } from '../firebase';
import { 
  isSignInWithEmailLink, 
  signInWithEmailLink,
  setPersistence,
  browserLocalPersistence,
  onIdTokenChanged,
  signOut as firebaseSignOut
} from 'firebase/auth';

const AuthContext = createContext(null);
const AUTH_EMAIL_KEY = 'auth_email_for_sign_in';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [emailLinkError, setEmailLinkError] = useState(null);

  useEffect(() => {
    const setAuthPersistence = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
        console.log('Auth persistence set to local');
      } catch (error) {
        console.error('Error setting auth persistence:', error);
      }
    };

    setAuthPersistence();

    // Listen for token changes across tabs/windows
    const unsubscribeToken = onIdTokenChanged(auth, async (user) => {
      if (user) {
        // Refresh token handling
        try {
          const token = await user.getIdToken();
          localStorage.setItem('authToken', token);
        } catch (error) {
          console.error('Error refreshing token:', error);
        }
      } else {
        localStorage.removeItem('authToken');
      }
    });

    // Listen for storage events from other tabs/windows
    const storageListener = (event) => {
      if (event.key === 'authToken') {
        // Force a refresh of the auth state
        auth.currentUser?.reload();
      }
    };

    window.addEventListener('storage', storageListener);

    return () => {
      unsubscribeToken();
      window.removeEventListener('storage', storageListener);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      console.log('Auth state changed:', user);
      setUser(user);
      if (!isSignInWithEmailLink(auth, window.location.href)) {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleEmailSignIn = async () => {
      if (!isSignInWithEmailLink(auth, window.location.href)) return;

      try {
        // Get email from URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        let email = urlParams.get('email');

        // If no email in URL, try session storage
        if (!email) {
          email = sessionStorage.getItem(AUTH_EMAIL_KEY);
        }

        // If still no email, try localStorage as fallback for browser handover
        if (!email) {
          email = localStorage.getItem(AUTH_EMAIL_KEY);
        }

        if (email) {
          await signInWithEmailLink(auth, email, window.location.href);
          // Store the token in localStorage
          const token = await auth.currentUser.getIdToken();
          localStorage.setItem('authToken', token);
          
          // Set a timestamp for when this link was first used
          const linkUsageTime = localStorage.getItem('auth_link_first_use');
          if (!linkUsageTime) {
            localStorage.setItem('auth_link_first_use', Date.now().toString());
          }
          
          // Only clear storage after link expires (1 hour)
          const firstUseTime = parseInt(localStorage.getItem('auth_link_first_use') || Date.now());
          if (Date.now() - firstUseTime >= 3600000) { // 1 hour
            sessionStorage.removeItem(AUTH_EMAIL_KEY);
            localStorage.removeItem(AUTH_EMAIL_KEY);
            localStorage.removeItem('auth_link_first_use');
          }
          
          window.history.replaceState(null, '', '/erstellen');
        } else {
          setEmailLinkError('No email found for sign in. Please request a new sign in link.');
          console.error('No email found for sign in');
        }
      } catch (error) {
        console.error('Email sign in error:', error);
        setEmailLinkError('Failed to complete sign in. Please request a new sign in link.');
      } finally {
        setLoading(false);
      }
    };

    handleEmailSignIn();
  }, []);

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      localStorage.removeItem('authToken');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    authError,
    emailLinkError,
    signOut,
    clearAuthError: () => {
      setAuthError(null);
      setEmailLinkError(null);
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};